import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import Checkbox from '@mui/material/Checkbox';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import * as React from 'react';
import { useUpdateClientField } from '../../../../sic-lib/api/hooks/useClientFields';
import { FieldValidationType } from '../../../../sic-lib/enums/FieldValidationType';
import { GenericReducer, genericReducer } from '../../../../sic-lib/reducers/genericReducer';
import { ClientField, buildUpdateClientField } from '../../../../sic-lib/types/ClientField';
import { IErrorFormValidation } from '../../../../sic-lib/utils/formValidationHelpers';
import IconButtonLoading from '../../../Shared/IconButtonLoading/IconButtonLoading';

interface FormState {
    fieldLabel: string;
    fieldIsUnique: boolean;
    fieldIsFilterable: boolean;
    formError: IErrorFormValidation | null;
    isVerifiable: boolean;
    isVerified: boolean;
}

export interface Props {
    config: ClientField;
    onToggleHandler: () => void;
}

export default function ClientFieldsItemEdit({ config, onToggleHandler }: Props) {
    const { mutate: updateClientField, isPending: isLoading } = useUpdateClientField();

    const [formState, formDispatch] = React.useReducer<GenericReducer<FormState>>(genericReducer, {
        fieldLabel: config.label,
        fieldIsUnique: config.isUnique,
        fieldIsFilterable: config.isFilterable,
        formError: null,
        isVerifiable: config.isVerifiable,
        isVerified: config.isVerified,
    });

    const handleChangeCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
        formDispatch({ [event.target.name]: event.target.checked });
    };

    const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
        formDispatch({ [event.target.name]: event.target.value });
    };

    const handleSubmit = async () => {
        try {
            const field = buildUpdateClientField({
                ...config,
                ...{
                    label: formState.fieldLabel,
                    isUnique: formState.fieldIsUnique,
                    isFilterable: formState.fieldIsFilterable,
                    isVerifiable: formState.isVerifiable,
                    isVerified: formState.isVerified,
                },
            });

            updateClientField(field);

            onToggleHandler();
        } catch (error: any) {
            if (error.isFormError) {
                formDispatch({ formError: error as IErrorFormValidation });
            }
        }
    };

    return (
        <ClickAwayListener onClickAway={onToggleHandler}>
            <Grid container direction="row" wrap="wrap">
                <Grid item xs={true}>
                    <Grid container direction="column" wrap="nowrap">
                        <Grid item xs="auto">
                            <TextField
                                id="text_field_label"
                                label="Field label"
                                name="fieldLabel"
                                error={Boolean(formState.formError?.field === 'label')}
                                helperText={
                                    formState.formError?.field === 'label'
                                        ? formState.formError.message
                                        : ''
                                }
                                value={formState.fieldLabel}
                                onChange={handleChangeText}
                                autoFocus
                                required
                            />
                        </Grid>
                        {/* checkboxes */}
                        <Grid item xs="auto" container direction="row">
                            <Grid item xs="auto">
                                <FormControlLabel
                                    label="Unique value"
                                    labelPlacement="end"
                                    control={
                                        <Checkbox
                                            color="primary"
                                            name="fieldIsUnique"
                                            checked={formState.fieldIsUnique}
                                            onChange={handleChangeCheckbox}
                                        />
                                    }
                                />
                            </Grid>
                            <Grid item xs="auto">
                                <FormControlLabel
                                    label="Filterable field"
                                    labelPlacement="end"
                                    control={
                                        <Checkbox
                                            color="primary"
                                            name="fieldIsFilterable"
                                            checked={formState.fieldIsFilterable}
                                            onChange={handleChangeCheckbox}
                                        />
                                    }
                                />
                            </Grid>
                            {(config.validationType === FieldValidationType.usMobilePhone ||
                                config.validationType === FieldValidationType.email) && (
                                <Grid item xs="auto">
                                    <FormControlLabel
                                        label="Is verifiable field"
                                        labelPlacement="end"
                                        control={
                                            <Checkbox
                                                color="primary"
                                                name="isVerifiable"
                                                checked={formState.isVerifiable}
                                                onChange={handleChangeCheckbox}
                                            />
                                        }
                                    />
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs="auto">
                    <Grid container direction="row" wrap="nowrap">
                        <Grid item xs="auto">
                            <IconButtonLoading
                                tooltip="Save"
                                showLoading={isLoading}
                                color="secondary"
                                onClick={handleSubmit}
                            >
                                <DoneIcon />
                            </IconButtonLoading>
                        </Grid>
                        <Grid item xs="auto">
                            <IconButtonLoading
                                tooltip="Cancel"
                                showLoading={false}
                                color="primary"
                                onClick={onToggleHandler}
                            >
                                <CloseIcon />
                            </IconButtonLoading>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </ClickAwayListener>
    );
}
